/**
 * Lot balance words and the "where the kilos went" breakdown (F7.9-a). Accounted = kilos still in
 * the lot's own bags + everything made from it (share-weighted); lost = gate net − accounted.
 */
import { humanize } from '../../labels'
import type { LotBalanceRow } from './mappers'

export const FLAG_LABEL: Record<string, string> = {
  ACCOUNTED_BELOW_LINE: 'Less than 98 % accounted for',
  MORE_PACKED_THAN_DELIVERED: 'More packed than the farmer delivered',
}
/** Plain words for a flag; the line comes from the server (98 by default). */
export function flagLabel(code: string, minPct = 98): string {
  if (code === 'ACCOUNTED_BELOW_LINE') return `Less than ${minPct} % accounted for`
  return FLAG_LABEL[code] ?? humanize(code)
}

/** "KHARIF-2026" → "Kharif 2026". */
export function seasonLabel(code: string | null | undefined): string {
  if (!code) return '—'
  return code
    .split(/[-_\s]+/)
    .map((p) => (/^\d+$/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))
    .join(' ')
}

export type SegmentKey =
  | 'inBags'
  | 'brandBatch'
  | 'convBatch'
  | 'byproduct'
  | 'waste'
  | 'packedWaiting'
  | 'packedReleased'
  | 'packedHeld'
  | 'packedDestroyed'
  | 'elsewhere'
  | 'lost'

export interface Segment {
  key: SegmentKey
  label: string
  grams: number
  /** Share of the gate net (0–100), for the bar width. */
  pct: number
}

export const SEGMENT_LABEL: Record<SegmentKey, string> = {
  inBags: 'Still in the lot’s bags',
  brandBatch: 'Brand batches',
  convBatch: 'Ordinary batches',
  byproduct: 'By-product',
  waste: 'Waste',
  packedWaiting: 'Packed, waiting for release',
  packedReleased: 'Packed and released',
  packedHeld: 'Packed, on hold',
  packedDestroyed: 'Packed, destroyed',
  elsewhere: 'Other outputs',
  lost: 'Not accounted for',
}

/**
 * Where the kilos went, in display order, empty parts left out. The parts add up to the gate net:
 * the named parts, then "other outputs" for anything accounted but not in a named part, then the
 * kilos not accounted for. A lot over-accounted (lost < 0) has no "not accounted" part.
 */
export function balanceSegments(b: LotBalanceRow): Segment[] {
  const named: Array<[SegmentKey, number]> = [
    ['inBags', b.inLotBagsG],
    ['brandBatch', b.brandBatchG],
    ['convBatch', b.convBatchG],
    ['byproduct', b.byproductG],
    ['waste', b.wasteG],
    ['packedWaiting', b.packedWaitingG],
    ['packedReleased', b.packedReleasedG],
    ['packedHeld', b.packedHeldG],
    ['packedDestroyed', b.packedDestroyedG],
  ]
  const namedSum = named.reduce((s, [, g]) => s + g, 0)
  const elsewhere = b.accountedG - namedSum
  const parts: Array<[SegmentKey, number]> = [...named, ['elsewhere', elsewhere > 0 ? elsewhere : 0], ['lost', b.lostG > 0 ? b.lostG : 0]]
  const whole = Math.max(b.gateNetG, b.accountedG + Math.max(b.lostG, 0), 1)
  return parts
    .filter(([, g]) => g > 0)
    .map(([key, grams]) => ({ key, label: SEGMENT_LABEL[key], grams, pct: (grams * 100) / whole }))
}

/** Tone of the accounted share against the line. */
export function accountedTone(b: Pick<LotBalanceRow, 'accountedPct' | 'accountedMinPct' | 'overPacked'>): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (b.overPacked) return 'danger'
  if (b.accountedPct === null) return 'neutral'
  return b.accountedPct < b.accountedMinPct ? 'danger' : 'ok'
}

/** "97.52 %" or "—". */
export function pctText(p: number | null): string {
  return p === null ? '—' : `${p.toFixed(2).replace(/\.?0+$/, '')} %`
}
